import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Save, RefreshCw, MousePointer, Menu, FileText, Send, Code, Copy, CheckCircle, Gamepad2, Flame } from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';

const CMSSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/cms/site-settings');
      setSettings(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/cms/site-settings', settings);
      toast.success('Paramètres mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Paramètres du site</h1>
        <p className="text-slate-500">Configurez le bouton header, le menu flottant et le footer</p>
      </div>

      {settings && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MousePointer className="w-5 h-5" />
                Bouton Header
              </CardTitle>
              <CardDescription>Le bouton "Espace Client" dans la barre de navigation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-2">
                  <Label>Texte du bouton</Label>
                  <Input
                    value={settings.header_button?.text || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      header_button: { ...settings.header_button, text: e.target.value }
                    })}
                    placeholder="Espace Client"
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL de destination</Label>
                  <Input
                    value={settings.header_button?.url || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      header_button: { ...settings.header_button, url: e.target.value }
                    })}
                    placeholder="/login ou https://..."
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={settings.header_button?.open_new_tab === true}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      header_button: { ...settings.header_button, open_new_tab: checked }
                    })}
                  />
                  <Label>Nouvel onglet</Label>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={settings.header_button?.is_visible !== false}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      header_button: { ...settings.header_button, is_visible: checked }
                    })}
                  />
                  <Label>Afficher le bouton</Label>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Menu className="w-5 h-5" />
                Menu flottant
              </CardTitle>
              <CardDescription>Les boutons flottants à droite du site</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={settings.floating_menu?.show_scroll_top !== false}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      floating_menu: { ...settings.floating_menu, show_scroll_top: checked }
                    })}
                  />
                  <Label>Retour en haut</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={settings.floating_menu?.show_brochure !== false}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      floating_menu: { ...settings.floating_menu, show_brochure: checked }
                    })}
                  />
                  <Label>Télécharger brochure</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={settings.floating_menu?.show_callback !== false}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      floating_menu: { ...settings.floating_menu, show_callback: checked }
                    })}
                  />
                  <Label>Être rappelé</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={settings.floating_menu?.show_quote !== false}
                    onCheckedChange={(checked) => setSettings({
                      ...settings,
                      floating_menu: { ...settings.floating_menu, show_quote: checked }
                    })}
                  />
                  <Label>Devis rapide</Label>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Footer
              </CardTitle>
              <CardDescription>Pied de page du site</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Texte copyright</Label>
                  <Input
                    value={settings.footer?.copyright_text || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      footer: { ...settings.footer, copyright_text: e.target.value }
                    })}
                    placeholder="MSC L'Agence. Tous droits réservés."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Version</Label>
                  <Input
                    value={settings.footer?.version || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      footer: { ...settings.footer, version: e.target.value }
                    })}
                    placeholder="1.0.0"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={settings.footer?.show_admin_icon !== false}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    footer: { ...settings.footer, show_admin_icon: checked }
                  })}
                />
                <Label>Afficher l'icône Admin (à côté de CGV)</Label>
              </div>
            </CardContent>
          </Card>

          {/* Lead Pages Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Pages de capture de leads
              </CardTitle>
              <CardDescription>
                Configurez la redirection vers vos landing pages externes pour les demandes de devis et de rappel
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={settings.lead_pages?.use_external_pages === true}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    lead_pages: { ...settings.lead_pages, use_external_pages: checked }
                  })}
                />
                <Label>Rediriger vers des pages externes (landing pages)</Label>
              </div>
              
              {settings.lead_pages?.use_external_pages && (
                <div className="space-y-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label>URL de la page de demande de devis</Label>
                    <Input
                      value={settings.lead_pages?.quote_page_url || ''}
                      onChange={(e) => setSettings({
                        ...settings,
                        lead_pages: { ...settings.lead_pages, quote_page_url: e.target.value }
                      })}
                      placeholder="https://votre-outil.com/demande-devis"
                    />
                    <p className="text-xs text-slate-500">
                      Les visiteurs seront redirigés vers cette page quand ils cliquent sur "Demander un devis"
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>URL de la page de demande de rappel</Label>
                    <Input
                      value={settings.lead_pages?.callback_page_url || ''}
                      onChange={(e) => setSettings({
                        ...settings,
                        lead_pages: { ...settings.lead_pages, callback_page_url: e.target.value }
                      })}
                      placeholder="https://votre-outil.com/demande-rappel"
                    />
                    <p className="text-xs text-slate-500">
                      Les visiteurs seront redirigés vers cette page quand ils cliquent sur "Être rappelé"
                    </p>
                  </div>
                </div>
              )}

              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mt-4">
                <p className="text-sm text-amber-800">
                  <strong>Note :</strong> Si les redirections sont désactivées, les formulaires intégrés seront affichés sur le site.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Game Section Toggle */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gamepad2 className="w-5 h-5" />
                Section Jeux Concours / Espace Joueur
              </CardTitle>
              <CardDescription>
                Activez ou désactivez l'affichage de la section jeux concours sur le site public
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Switch
                  data-testid="game-section-toggle"
                  checked={settings.game_section?.enabled === true}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    game_section: { ...settings.game_section, enabled: checked }
                  })}
                />
                <Label>Afficher la section jeux concours sur le site</Label>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-600">
                  <strong>Quand désactivé :</strong> Les boutons flottants (Trophée, Coffre), la popup de jeu, les liens vers l'espace joueur et la page du Coffre MSC seront masqués du site public. Le back-office CMS reste accessible.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Copilote Section Toggle */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Flame className="w-5 h-5" />
                Section Copilote MSC
              </CardTitle>
              <CardDescription>
                Activez ou désactivez l'affichage de la section Copilote sur le site public
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Switch
                  data-testid="copilote-section-toggle"
                  checked={settings.copilote_section?.enabled === true}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    copilote_section: { ...settings.copilote_section, enabled: checked }
                  })}
                />
                <Label>Afficher la section Copilote sur le site</Label>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-600">
                  <strong>Quand désactivé :</strong> Le bouton Copilote dans la navigation, le formulaire de candidature, et la page Copilote seront masqués du site public.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Lead Forwarding Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="w-5 h-5" />
                Transfert des leads (API)
              </CardTitle>
              <CardDescription>
                Configurez le transfert automatique des données vers votre outil externe (optionnel)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={settings.lead_forwarding?.enabled === true}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    lead_forwarding: { ...settings.lead_forwarding, enabled: checked }
                  })}
                />
                <Label>Transférer les leads vers un endpoint API</Label>
              </div>
              
              {settings.lead_forwarding?.enabled && (
                <div className="space-y-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label>URL de l'endpoint externe</Label>
                    <Input
                      value={settings.lead_forwarding?.external_endpoint || ''}
                      onChange={(e) => setSettings({
                        ...settings,
                        lead_forwarding: { ...settings.lead_forwarding, external_endpoint: e.target.value }
                      })}
                      placeholder="https://votre-outil.com/api/leads"
                    />
                    <p className="text-xs text-slate-500">
                      Les données seront envoyées en POST avec les champs : name, email, phone, company, message, form_type, page_url
                    </p>
                  </div>
                </div>
              )}

              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Code className="w-4 h-4 text-slate-600" />
                  <span className="font-medium text-slate-700">Code embed pour sites externes</span>
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Copiez ce code pour intégrer un formulaire de lead sur un autre site. Les données seront automatiquement enregistrées ici.
                </p>
                <EmbedCodeGenerator />
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-cyan-500 to-blue-600">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
};

// Component to generate embed codes
const EmbedCodeGenerator = () => {
  const [copied, setCopied] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  
  const generateEmbedCode = (type) => {
    const config = {
      callback: {
        type: "callback",
        title: "Demande de rappel",
        description: "Laissez-nous vos coordonnées, nous vous rappelons rapidement.",
        buttonText: "Être rappelé",
        primaryColor: "#55c2dd",
        backgroundColor: "#ffffff",
        borderRadius: "12",
        showPhone: true,
        showEmail: true,
        showMessage: false,
        showCompany: false,
        capturePageUrl: true,
        successMessage: "Votre demande a bien été envoyée. Nous vous recontacterons rapidement.",
        apiEndpoint: `${backendUrl}/api/leads/embed`
      },
      quote: {
        type: "quote",
        title: "Demande de devis",
        description: "Décrivez votre projet, nous vous envoyons un devis personnalisé.",
        buttonText: "Envoyer ma demande",
        primaryColor: "#f97316",
        backgroundColor: "#ffffff",
        borderRadius: "12",
        showPhone: true,
        showEmail: true,
        showMessage: true,
        showCompany: true,
        capturePageUrl: true,
        successMessage: "Votre demande de devis a bien été envoyée. Nous vous répondrons sous 24-48h.",
        apiEndpoint: `${backendUrl}/api/leads/embed`
      }
    };
    
    const configStr = btoa(encodeURIComponent(JSON.stringify(config[type])));
    
    return `<!-- MSC Lead Form - ${type === 'callback' ? 'Demande de rappel' : 'Demande de devis'} -->
<div id="msc-lead-form-${type}"></div>
<script>
(function() {
  var config = JSON.parse(decodeURIComponent(atob('${configStr}')));
  var container = document.getElementById('msc-lead-form-${type}');
  if (!container) return;
  
  var style = document.createElement('style');
  style.textContent = \`
    .msc-form-container { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 400px; margin: 0 auto; padding: 24px; background: \${config.backgroundColor}; border-radius: \${config.borderRadius}px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
    .msc-form-title { font-size: 20px; font-weight: 600; color: #1a1a1a; margin-bottom: 8px; }
    .msc-form-desc { font-size: 14px; color: #666; margin-bottom: 20px; }
    .msc-form-group { margin-bottom: 16px; }
    .msc-form-label { display: block; font-size: 14px; font-weight: 500; color: #374151; margin-bottom: 6px; }
    .msc-form-input, .msc-form-textarea { width: 100%; padding: 10px 14px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; box-sizing: border-box; }
    .msc-form-input:focus, .msc-form-textarea:focus { outline: none; border-color: \${config.primaryColor}; }
    .msc-form-textarea { min-height: 100px; resize: vertical; }
    .msc-form-btn { width: 100%; padding: 12px 20px; background: \${config.primaryColor}; color: white; border: none; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer; }
    .msc-form-btn:hover { opacity: 0.9; }
    .msc-form-btn:disabled { opacity: 0.6; cursor: not-allowed; }
    .msc-form-success { text-align: center; padding: 20px; }
    .msc-form-error { background: #fef2f2; color: #dc2626; padding: 10px; border-radius: 6px; font-size: 14px; margin-bottom: 16px; }
  \`;
  document.head.appendChild(style);
  
  container.innerHTML = \`
    <div class="msc-form-container">
      <div class="msc-form-title">\${config.title}</div>
      <div class="msc-form-desc">\${config.description}</div>
      <form id="msc-form-\${config.type}">
        <div class="msc-form-group"><label class="msc-form-label">Nom complet *</label><input type="text" name="name" class="msc-form-input" required></div>
        \${config.showPhone ? '<div class="msc-form-group"><label class="msc-form-label">Téléphone *</label><input type="tel" name="phone" class="msc-form-input" required></div>' : ''}
        \${config.showEmail ? '<div class="msc-form-group"><label class="msc-form-label">Email</label><input type="email" name="email" class="msc-form-input"></div>' : ''}
        \${config.showCompany ? '<div class="msc-form-group"><label class="msc-form-label">Société</label><input type="text" name="company" class="msc-form-input"></div>' : ''}
        \${config.showMessage ? '<div class="msc-form-group"><label class="msc-form-label">Votre projet *</label><textarea name="message" class="msc-form-textarea" required></textarea></div>' : ''}
        <input type="hidden" name="page_url" value="">
        <input type="hidden" name="form_type" value="\${config.type}">
        <div id="msc-error-\${config.type}" class="msc-form-error" style="display:none;"></div>
        <button type="submit" class="msc-form-btn">\${config.buttonText}</button>
      </form>
      <div id="msc-success-\${config.type}" class="msc-form-success" style="display:none;">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="\${config.primaryColor}" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
        <div style="font-size:18px;font-weight:600;margin:12px 0 8px;">Merci !</div>
        <div style="color:#666;">\${config.successMessage}</div>
      </div>
    </div>
  \`;
  
  if (config.capturePageUrl) document.querySelector('#msc-form-\${config.type} input[name="page_url"]').value = window.location.href;
  
  document.getElementById('msc-form-\${config.type}').addEventListener('submit', function(e) {
    e.preventDefault();
    var btn = this.querySelector('button');
    var err = document.getElementById('msc-error-\${config.type}');
    btn.disabled = true; btn.textContent = 'Envoi...'; err.style.display = 'none';
    var data = {}; new FormData(this).forEach(function(v,k){data[k]=v;});
    fetch(config.apiEndpoint, { method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(data) })
    .then(function(r){return r.json();})
    .then(function(res){ if(res.success){ document.getElementById('msc-form-\${config.type}').style.display='none'; document.getElementById('msc-success-\${config.type}').style.display='block'; } else { throw new Error(res.message||'Erreur'); }})
    .catch(function(e){ err.textContent=e.message; err.style.display='block'; btn.disabled=false; btn.textContent=config.buttonText; });
  });
})();
</script>`;
  };

  const copyToClipboard = (type) => {
    const code = generateEmbedCode(type);
    navigator.clipboard.writeText(code);
    setCopied(type);
    toast.success('Code copié !');
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => copyToClipboard('callback')}
          className="flex-1"
        >
          {copied === 'callback' ? <CheckCircle className="w-4 h-4 mr-2 text-green-500" /> : <Copy className="w-4 h-4 mr-2" />}
          Formulaire Rappel
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => copyToClipboard('quote')}
          className="flex-1"
        >
          {copied === 'quote' ? <CheckCircle className="w-4 h-4 mr-2 text-green-500" /> : <Copy className="w-4 h-4 mr-2" />}
          Formulaire Devis
        </Button>
      </div>
    </div>
  );
};

export default CMSSettingsPage;
